// Agent widget context (docs/design/2026-07-17-agent-panel.md, P1 Task 4).
// Holds the page-awareness contract the widget sends to the chat Lambda, plus
// open/seed state. No UI here — the widget (AgentWidget) consumes this.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";

export interface AgentPageContext {
  route: string;
  entity?: { type: string; id: string; name?: string };
  tab?: string;
  /** Compact "what's on screen" summary (S1) — ids and numbers, never prose. */
  snapshot?: Record<string, unknown>;
}

export interface AgentSeed {
  kind: "explain" | "ask" | "draft-ticket";
  snapshot?: Record<string, unknown>;
  prompt?: string;
  modelHint?: "default" | "smart";
  /** Seed for draft-ticket mode (Tickets tab → widget draft flow). */
  ticket?: { key: string; summary: string };
}

interface AgentContextValue {
  pageContext: AgentPageContext | null;
  /** Always-latest context for the widget's request body (avoids stale closures). */
  pageContextRef: React.MutableRefObject<AgentPageContext | null>;
  setPageContext(ctx: AgentPageContext | null): void;
  /** Merge a "what's on screen" snapshot into the current context (S1). */
  setPageSnapshot(snapshot: Record<string, unknown> | null): void;
  open: boolean;
  openAgent(seed?: AgentSeed): void;
  closeAgent(): void;
  pendingSeed: AgentSeed | null;
  consumeSeed(): AgentSeed | null;
  /** Divider label enqueued when the page changes while the widget is open. */
  pendingContextNote: string | null;
  consumeContextNote(): string | null;
  /** Item the user pointed at (S2). Rides with the next message, then cleared. */
  pendingReference: AgentReference | null;
  pendingReferenceRef: React.MutableRefObject<AgentReference | null>;
  addReference(ref: AgentReference): void;
  clearReference(): void;
}

export interface AgentReference {
  label: string;
  snapshot: Record<string, unknown>;
}

const Ctx = createContext<AgentContextValue | null>(null);

/** URL-derived baseline context — the floor under explicit page registration,
 *  so the agent is never blind on a route no page has instrumented. Pages that
 *  call setPageContext still win (they add names, tabs, snapshots). */
function deriveContext(pathname: string, search: string): AgentPageContext {
  const tab = new URLSearchParams(search).get("tab") ?? undefined;
  let m = /^\/customers(?:-mui)?\/([^/]+)/.exec(pathname);
  if (m) return { route: pathname, entity: { type: "customer", id: decodeURIComponent(m[1]) }, tab };
  m = /^\/projects\/([^/]+)/.exec(pathname);
  if (m) return { route: pathname, entity: { type: "project", id: decodeURIComponent(m[1]) }, tab };
  return { route: pathname, tab };
}

/** Human label for a context transition, e.g. "Finance on avcraft" / "Today". */
function noteFor(ctx: AgentPageContext): string {
  const where = ctx.entity?.name ?? ctx.entity?.id;
  if (where && ctx.tab) return `${ctx.tab} on ${where}`;
  if (where) return where;
  if (ctx.tab) return ctx.tab;
  return ctx.route;
}

export function AgentProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const pageContextRef = useRef<AgentPageContext | null>(null);
  const [explicit, setExplicit] = useState<AgentPageContext | null>(null);
  const [snapshot, setSnapshot] = useState<Record<string, unknown> | null>(null);
  // Keep-open preference (P2): open state persists across tabs/sessions via
  // localStorage; the transcript stays per-tab in sessionStorage.
  const [open, setOpen] = useState(() => localStorage.getItem("agent-open") === "1");
  const [pendingSeed, setPendingSeed] = useState<AgentSeed | null>(null);
  const [pendingContextNote, setPendingContextNote] = useState<string | null>(null);
  const [pendingReference, setPendingReference] = useState<AgentReference | null>(null);
  const pendingReferenceRef = useRef<AgentReference | null>(null);
  const openRef = useRef(open);

  // Explicit page registration wins; the URL-derived floor covers everything else.
  // The snapshot rides on top of either (S1) — a change to it never counts as a move.
  const pageContext = useMemo(() => {
    const base = explicit ?? deriveContext(location.pathname, location.search);
    return snapshot ? { ...base, snapshot } : base;
  }, [explicit, snapshot, location.pathname, location.search]);

  // If the widget is open and we've moved to a different record/tab/page, enqueue
  // a divider so the conversation shows the shift (the widget also narrates it to
  // the model as a "[context] moved to …" message). A page enriching the derived
  // context for the same entity (adding name/tab) is a refinement, not a move.
  useEffect(() => {
    const prev = pageContextRef.current;
    pageContextRef.current = pageContext;
    if (!openRef.current || !prev) return;
    const moved =
      prev.entity?.id !== pageContext.entity?.id ||
      (!!prev.tab && !!pageContext.tab && prev.tab !== pageContext.tab) ||
      (!prev.entity && !pageContext.entity && prev.route !== pageContext.route);
    if (moved) setPendingContextNote(noteFor(pageContext));
  }, [pageContext]);

  const setPageContext = useCallback((ctx: AgentPageContext | null) => {
    setExplicit(ctx);
  }, []);

  const setPageSnapshot = useCallback((snap: Record<string, unknown> | null) => {
    setSnapshot(snap);
  }, []);

  const openAgent = useCallback((seed?: AgentSeed) => {
    if (seed) setPendingSeed(seed);
    openRef.current = true;
    setOpen(true);
    try { localStorage.setItem("agent-open", "1"); } catch { /* ignore */ }
  }, []);

  const closeAgent = useCallback(() => {
    openRef.current = false;
    setOpen(false);
    try { localStorage.removeItem("agent-open"); } catch { /* ignore */ }
  }, []);

  const consumeSeed = useCallback((): AgentSeed | null => {
    const s = pendingSeed;
    if (s) setPendingSeed(null);
    return s;
  }, [pendingSeed]);

  const consumeContextNote = useCallback((): string | null => {
    const n = pendingContextNote;
    if (n) setPendingContextNote(null);
    return n;
  }, [pendingContextNote]);

  const addReference = useCallback((ref: AgentReference) => {
    pendingReferenceRef.current = ref;
    setPendingReference(ref);
    openRef.current = true;
    setOpen(true);
    try { localStorage.setItem("agent-open", "1"); } catch { /* ignore */ }
  }, []);

  const clearReference = useCallback(() => {
    pendingReferenceRef.current = null;
    setPendingReference(null);
  }, []);

  const value = useMemo<AgentContextValue>(
    () => ({
      pageContext,
      pageContextRef,
      setPageContext,
      setPageSnapshot,
      open,
      openAgent,
      closeAgent,
      pendingSeed,
      consumeSeed,
      pendingContextNote,
      consumeContextNote,
      pendingReference,
      pendingReferenceRef,
      addReference,
      clearReference,
    }),
    [pageContext, setPageContext, setPageSnapshot, open, openAgent, closeAgent, pendingSeed, consumeSeed, pendingContextNote, consumeContextNote, pendingReference, addReference, clearReference],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAgent(): AgentContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAgent must be used within <AgentProvider>");
  return v;
}

/** Publish a compact "what's on screen" snapshot for the current page (S1).
 *  Re-runs when the snapshot's contents change; clears on unmount. Keep it to
 *  ids and numbers (cap arrays ~10) — it's sent with every message. */
export function useAgentSnapshot(snapshot: Record<string, unknown> | null): void {
  const { setPageSnapshot } = useAgent();
  const key = snapshot ? JSON.stringify(snapshot) : null;
  useEffect(() => {
    setPageSnapshot(key ? (JSON.parse(key) as Record<string, unknown>) : null);
    return () => setPageSnapshot(null);
  }, [key, setPageSnapshot]);
}
